import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { auth, sendPhoneChangeCodeFn, verifyPhoneChangeCodeFn } from "../../firebase";
import {
  updatePassword, reauthenticateWithCredential, EmailAuthProvider,
} from "firebase/auth";
import { IC, Field, Btn } from "../shared";

// ── 6-Digit Code Input ───────────────────────────────────────────────────────
const CodeInput = ({ value, onChange }) => {
  const inputRef = useRef(null);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 100); }, []);
  const digits = value.padEnd(6, " ").slice(0, 6).split("");

  return (
    <div style={{ position: "relative" }}>
      {/* Visual boxes */}
      <div style={{ display: "flex", gap: 8, justifyContent: "center", pointerEvents: "none" }}>
        {digits.map((d, i) => (
          <div key={i} style={{
            width: 44, height: 54, borderRadius: 14,
            border: `2px solid ${d.trim() ? "#2b5be0" : "rgba(43,91,224,.25)"}`,
            background: d.trim() ? "rgba(43,91,224,.08)" : "var(--paper)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 24, fontFamily: "'Syne', sans-serif", fontWeight: 800,
            color: "var(--ink)", transition: "all .15s",
            boxShadow: d.trim() ? "0 0 0 3px rgba(43,91,224,.12)" : "none",
          }}>
            {d.trim() || ""}
          </div>
        ))}
      </div>
      {/* Hidden real input on top */}
      <input
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={value}
        onChange={e => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        style={{
          position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
          opacity: 0, fontSize: 24, caretColor: "transparent",
        }}
      />
    </div>
  );
};

// ── Verification Code Modal ──────────────────────────────────────────────────
const VerifyCodeModal = ({ email, onVerify, onCancel, verifying, error }) => {
  const [code, setCode] = useState("");
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div className="amodal" style={{ background: "var(--card)", borderRadius: 24, padding: "32px 24px 28px", maxWidth: 360, width: "100%", textAlign: "center", boxShadow: "var(--shadow-xl)", border: "1.5px solid var(--border)" }}>
        {/* Icon */}
        <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(43,91,224,.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <svg style={{ width: 26, height: 26, color: "var(--accent)" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
        </div>
        <h3 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 20, color: "var(--ink)", marginBottom: 6 }}>Verify Phone Change</h3>
        <p style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.5, marginBottom: 24 }}>
          We sent a 6-digit code to<br/><strong style={{ color: "var(--ink)" }}>{email}</strong>
        </p>

        {/* Code Input */}
        <div style={{ marginBottom: 20 }}>
          <CodeInput value={code} onChange={setCode}/>
        </div>

        <p style={{ fontSize: 11, color: "var(--muted)", marginBottom: 16 }}>Code expires in 10 minutes</p>

        {error && (
          <div style={{ background: "var(--error-bg)", border: "1px solid var(--error-border)", borderRadius: 10, padding: "10px 14px", color: "#c0392b", fontSize: 13, fontWeight: 500, marginBottom: 14, textAlign: "left" }}>{error}</div>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: 14, borderRadius: 14, fontFamily: "Syne", fontWeight: 700, fontSize: 14, background: "var(--warm)", color: "var(--muted)", border: "none", cursor: "pointer" }}>Cancel</button>
          <button onClick={() => onVerify(code)} disabled={verifying || code.length < 6} style={{ flex: 1, padding: 14, borderRadius: 14, fontFamily: "Syne", fontWeight: 700, fontSize: 14, background: code.length === 6 ? "linear-gradient(135deg,#2b5be0,#5a80f0)" : "var(--warm)", color: code.length === 6 ? "#fff" : "var(--muted)", border: "none", cursor: "pointer", boxShadow: code.length === 6 ? "0 4px 14px rgba(43,91,224,.3)" : "none", opacity: verifying ? 0.6 : 1 }}>
            {verifying ? <span style={{ display: "inline-block", width: 16, height: 16, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "igo-spin .7s linear infinite" }}/> : "Verify"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Edit Profile Screen ──────────────────────────────────────────────────────
const EditProfile = ({ currentUser, onBack, onPhoneUpdate }) => {
  // ── Password ──
  const [pwForm, setPwForm]       = useState({ current: "", next: "", confirm: "" });
  const [pwErr, setPwErr]         = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwBusy, setPwBusy]       = useState(false);

  // ── Phone ──
  const [phone, setPhone]           = useState(currentUser.phone || "");
  const [phoneStep, setPhoneStep]   = useState("idle"); // idle | sending | modal | done
  const [phoneErr, setPhoneErr]     = useState("");
  const [verifying, setVerifying]   = useState(false);

  const handleChangePassword = async () => {
    setPwErr("");
    if (!pwForm.current || !pwForm.next || !pwForm.confirm) { setPwErr("Please fill all fields."); return; }
    if (pwForm.next.length < 6) { setPwErr("New password must be at least 6 characters."); return; }
    if (pwForm.next !== pwForm.confirm) { setPwErr("New passwords do not match."); return; }
    setPwBusy(true);
    try {
      const user = auth.currentUser;
      const cred = EmailAuthProvider.credential(user.email, pwForm.current);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, pwForm.next);
      setPwSuccess(true);
      setPwForm({ current: "", next: "", confirm: "" });
      setTimeout(() => setPwSuccess(false), 3000);
    } catch (e) {
      setPwErr(e.code === "auth/wrong-password" || e.code === "auth/invalid-credential" ? "Current password is incorrect." : e.message || "Something went wrong.");
    }
    setPwBusy(false);
  };

  const handlePhoneSendCode = async () => {
    setPhoneErr("");
    if (!phone.trim()) { setPhoneErr("Enter a phone number."); return; }
    if (phone.trim() === currentUser.phone) { setPhoneErr("This is already your current number."); return; }
    setPhoneStep("sending");
    try {
      await sendPhoneChangeCodeFn({ newPhone: phone.trim() });
      setPhoneStep("modal");
    } catch (e) {
      setPhoneErr(e.message || "Failed to send verification code.");
      setPhoneStep("idle");
    }
  };

  const handlePhoneVerify = async (code) => {
    setPhoneErr("");
    if (code.length !== 6) { setPhoneErr("Enter the 6-digit code."); return; }
    setVerifying(true);
    try {
      const result = await verifyPhoneChangeCodeFn({ code });
      onPhoneUpdate?.(result.data.newPhone);
      setPhoneStep("done");
      setTimeout(() => setPhoneStep("idle"), 3000);
    } catch (e) {
      const msg = e.message || "Verification failed.";
      setPhoneErr(msg.includes("expired") ? "Code expired. Please request a new one." : msg.includes("Invalid") ? "Invalid code. Please try again." : msg);
    }
    setVerifying(false);
  };

  return (
    <div className="screen aslide" style={{ padding: "20px 16px 90px", overflowY: "auto" }}>
      <div className="desk-container-sm">
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button onClick={onBack} style={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--warm)", borderRadius: 12, border: "none", cursor: "pointer" }}>
            <span style={{ width: 18, height: 18, color: "var(--ink)" }}>{IC.back}</span>
          </button>
          <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 22, color: "var(--ink)" }}>Edit Profile</h2>
        </div>

        {/* ── Email (read-only) ── */}
        <div style={{ background: "var(--card)", borderRadius: 20, padding: 18, border: "1.5px solid var(--border)", marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", fontFamily: "Syne", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Email</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)" }}>{currentUser.email}</div>
        </div>

        {/* ── Phone Number ── */}
        <div style={{ background: "var(--card)", borderRadius: 20, padding: 18, border: "1.5px solid var(--border)", marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", fontFamily: "Syne", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Phone Number</div>

          {phoneStep === "done" ? (
            <div className="apop" style={{ background: "var(--success-bg)", border: "1.5px solid var(--success-border)", borderRadius: 12, padding: "12px 14px", color: "var(--success)", fontSize: 13, fontWeight: 500 }}>Phone number updated!</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Field
                placeholder="99-000000"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                type="tel"
              />
              {phoneErr && phoneStep === "idle" && <div style={{ background: "var(--error-bg)", border: "1px solid var(--error-border)", borderRadius: 10, padding: "10px 14px", color: "#c0392b", fontSize: 13, fontWeight: 500 }}>{phoneErr}</div>}
              {phone.trim() !== (currentUser.phone || "") && phoneStep !== "sending" && (
                <Btn variant="accent" onClick={handlePhoneSendCode} small>
                  Change Phone Number
                </Btn>
              )}
              {phoneStep === "sending" && (
                <Btn variant="accent" disabled small>
                  <span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "igo-spin .7s linear infinite" }}/>
                  Sending code...
                </Btn>
              )}
            </div>
          )}
        </div>

        {/* ── Change Password ── */}
        <div style={{ background: "var(--card)", borderRadius: 20, padding: 18, border: "1.5px solid var(--border)", marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", fontFamily: "Syne", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Change Password</div>
          {pwSuccess ? (
            <div className="apop" style={{ background: "var(--success-bg)", border: "1.5px solid var(--success-border)", borderRadius: 12, padding: "12px 14px", color: "var(--success)", fontSize: 13, fontWeight: 500 }}>Password updated successfully!</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Field label="Current password" type="password" placeholder="••••••••" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}/>
              <Field label="New password" type="password" placeholder="••••••••" value={pwForm.next} onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))}/>
              <Field label="Confirm new password" type="password" placeholder="••••••••" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}/>
              {pwErr && <div style={{ background: "var(--error-bg)", border: "1px solid var(--error-border)", borderRadius: 10, padding: "10px 14px", color: "#c0392b", fontSize: 13, fontWeight: 500 }}>{pwErr}</div>}
              <Btn variant="accent" onClick={handleChangePassword} disabled={pwBusy}>
                {pwBusy ? <span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "igo-spin .7s linear infinite" }}/> : "Update Password"}
              </Btn>
            </div>
          )}
        </div>
      </div>

      {/* ── Verification Code Popup ── */}
      {phoneStep === "modal" && (
        <VerifyCodeModal
          email={currentUser.email}
          onVerify={handlePhoneVerify}
          onCancel={() => { setPhoneStep("idle"); setPhoneErr(""); }}
          verifying={verifying}
          error={phoneErr}
        />
      )}
    </div>
  );
};

export default EditProfile;
